import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, CalendarClock, ChevronDown } from "lucide-react";
import { NAV_LINKS, LOGO_URL, CONTACT } from "../mock";
import { useEnquiry } from "../context/EnquiryContext";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const enquiry = useEnquiry();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  }, [location.pathname]);

  // Fixed cream menu at all times so the full AwareHub logo is always visible.
  const overHero = false;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-sand-50/95 backdrop-blur-md shadow-[0_4px_30px_rgba(31,56,53,0.08)] py-2"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 shrink-0">
          <img
            src={LOGO_URL}
            alt="AwareHub TMS"
            className="h-11 w-auto object-contain"
          />
        </Link>

        <ul className="hidden lg:flex items-center gap-0 xl:gap-1">
          {NAV_LINKS.map((link) => (
            <li key={link.path} className={link.children ? "relative group" : undefined}>
              <NavLink
                to={link.path}
                end={link.path === "/"}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative whitespace-nowrap px-2 xl:px-3.5 py-2 text-sm font-medium tracking-wide transition-colors duration-200 ${
                    overHero
                      ? isActive
                        ? "text-white"
                        : "text-white/85 hover:text-white"
                      : isActive
                      ? "text-brand-700"
                      : "text-brand-900/70 hover:text-brand-700"
                  } after:absolute after:left-2 after:right-2 xl:after:left-3.5 xl:after:right-3.5 after:-bottom-0.5 after:h-0.5 ${
                    overHero ? "after:bg-white" : "after:bg-brand-500"
                  } after:origin-left after:transition-transform after:duration-300 ${
                    isActive ? "after:scale-x-100" : "after:scale-x-0 hover:after:scale-x-100"
                  }`
                }
              >
                {link.label}
                {link.children && (
                  <ChevronDown className="inline-block ml-1 h-3.5 w-3.5 -mt-0.5 transition-transform duration-200 group-hover:rotate-180" />
                )}
              </NavLink>
              {link.children && (
                <div
                  className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 transition-[opacity,transform,visibility] duration-200"
                  data-testid="nav-about-dropdown"
                >
                  <ul className="min-w-[210px] rounded-2xl bg-white border border-brand-100 shadow-[0_24px_50px_-20px_rgba(31,56,53,0.35)] p-2">
                    {link.children.map((c) => (
                      <li key={c.path}>
                        <NavLink
                          to={c.path}
                          data-testid={`nav-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className={({ isActive }) =>
                            `block rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                              isActive ? "bg-brand-50 text-brand-700" : "text-brand-900/75 hover:bg-brand-50 hover:text-brand-700"
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-2">
          <a
            href={CONTACT.phoneHref}
            aria-label={`Call ${CONTACT.phone}`}
            data-testid="nav-phone-link"
            className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 xl:px-4 py-2.5 text-sm font-semibold transition-colors duration-200 ${
              overHero
                ? "text-white hover:bg-white/15"
                : "text-brand-700 hover:bg-brand-100"
            }`}
          >
            <Phone className="h-4 w-4" />
            <span className="hidden xl:inline">{CONTACT.phone}</span>
          </a>
          <button
            onClick={() => enquiry && enquiry.openEnquiry()}
            data-testid="nav-enquire-button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-cta-500 hover:bg-cta-600 text-white px-5 py-2.5 text-sm font-semibold transition-colors duration-200 shadow-sm"
          >
            <CalendarClock className="h-4 w-4" />
            Enquire Now
          </button>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
          className={`lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full transition-colors ${
            overHero ? "text-white hover:bg-white/15" : "text-brand-800 hover:bg-brand-100"
          }`}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[760px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="mx-4 mt-3 rounded-2xl bg-white shadow-xl border border-brand-100 p-3">
          <ul className="flex flex-col">
            {NAV_LINKS.map((link) => (
              <li key={link.path}>
                <NavLink
                  to={link.path}
                  end={link.path === "/"}
                  className={({ isActive }) =>
                    `block rounded-xl px-4 py-3 text-base font-medium transition-colors ${
                      isActive
                        ? "bg-brand-50 text-brand-700"
                        : "text-brand-900/80 hover:bg-brand-50"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
                {link.children?.map((c) => (
                  <NavLink
                    key={c.path}
                    to={c.path}
                    data-testid={`nav-mobile-sublink-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={({ isActive }) =>
                      `block rounded-xl pl-9 pr-4 py-2.5 text-[0.95rem] font-medium transition-colors ${
                        isActive ? "bg-brand-50 text-brand-700" : "text-brand-900/65 hover:bg-brand-50"
                      }`
                    }
                  >
                    {c.label}
                  </NavLink>
                ))}
              </li>
            ))}
          </ul>
          <button
            onClick={() => { setOpen(false); enquiry && enquiry.openEnquiry(); }}
            data-testid="nav-enquire-button-mobile"
            className="mt-2 w-full flex items-center justify-center gap-2 rounded-xl bg-cta-500 hover:bg-cta-600 text-white px-5 py-3 text-sm font-semibold"
          >
            <CalendarClock className="h-4 w-4" />
            Enquire Now
          </button>
          <a
            href={CONTACT.phoneHref}
            className="mt-2 flex items-center justify-center gap-2 rounded-xl border border-brand-200 text-brand-700 px-5 py-3 text-sm font-semibold"
          >
            <Phone className="h-4 w-4" />
            Call {CONTACT.phone}
          </a>
        </div>
      </div>
    </header>
  );
}
